import styled from 'styled-components'
import { COLORS, SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import type { SurveyQuestion } from '@bandos/shared'
import { ScaleQuestionChart } from './ScaleQuestionChart'
import { YesNoChart } from './YesNoChart'
import { MultipleChoiceChart } from './MultipleChoiceChart'
import { CheckboxChart } from './CheckboxChart'
import { OpenResponsesView } from './OpenResponsesView'

export interface QuestionInsightCardProps {
  question: SurveyQuestion
  questionIndex: number
  answers: (string | number | boolean | string[] | null)[]
  isPrinting: boolean
  isDeleted?: boolean
}

const TYPE_LABELS: Record<string, string> = {
  scale: 'Scale',
  yes_no: 'Yes / No',
  multiple_choice: 'Multiple choice',
  checkbox: 'Checkbox',
  open: 'Open text',
}

const Card = styled.div<{ $deleted?: boolean }>`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  padding: ${SPACING.xl};
  page-break-inside: avoid;
  overflow: hidden;
  opacity: ${(p) => (p.$deleted ? 0.75 : 1)};
`

const DeletedBadge = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${COLORS.gray500};
  background: ${COLORS.gray100};
  padding: 2px ${SPACING.sm};
  border-radius: ${BORDER_RADIUS.full};
  white-space: nowrap;
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.lg};
`

const QuestionMeta = styled.div`
  flex: 1;
`

const QuestionNumber = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const QuestionText = styled.h3`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: ${SPACING.xs} 0 0 0;
  line-height: 1.4;
`

const TypeBadge = styled.span`
  flex-shrink: 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${COLORS.gray500};
  background: ${COLORS.gray100};
  padding: 2px ${SPACING.sm};
  border-radius: ${BORDER_RADIUS.full};
  white-space: nowrap;
`

const ResponseCount = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin-bottom: ${SPACING.md};
`

export function QuestionInsightCard({
  question,
  questionIndex,
  answers,
  isPrinting,
  isDeleted = false,
}: QuestionInsightCardProps) {
  // Type-guard each answer to the expected type for this question.
  // If questions were edited after responses were collected (deleted, reordered,
  // or type-changed), old answers may be the wrong type — drop them silently
  // rather than passing garbage to charts.
  const scaleMax = question.scaleMax ?? 5
  const scaleAnswers = answers.filter(
    (a): a is number => typeof a === 'number' && a >= 1 && a <= scaleMax
  )
  const yesNoAnswers = answers.filter((a): a is boolean => typeof a === 'boolean')
  const mcAnswers = answers.filter(
    (a): a is string => typeof a === 'string' && (question.options ?? []).includes(a)
  )
  const validOpts = question.options ?? []
  const checkboxAnswers = answers.filter(
    (a): a is string[] =>
      Array.isArray(a) && a.every((v) => typeof v === 'string' && validOpts.includes(v))
  )
  const openAnswers = answers.filter((a): a is string => typeof a === 'string' && a.length > 0)

  const validAnswerCount = {
    scale: scaleAnswers.length,
    yes_no: yesNoAnswers.length,
    multiple_choice: mcAnswers.length,
    checkbox: checkboxAnswers.length,
    open: openAnswers.length,
  }[question.type]

  return (
    <Card $deleted={isDeleted}>
      <CardHeader>
        <QuestionMeta>
          {!isDeleted && <QuestionNumber>Q{questionIndex + 1}</QuestionNumber>}
          <QuestionText>{question.question}</QuestionText>
        </QuestionMeta>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
          {isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
          <TypeBadge>{TYPE_LABELS[question.type] ?? question.type}</TypeBadge>
        </div>
      </CardHeader>

      <ResponseCount>
        {validAnswerCount} response{validAnswerCount !== 1 ? 's' : ''}
      </ResponseCount>

      {question.type === 'scale' && (
        <ScaleQuestionChart answers={scaleAnswers} max={scaleMax} isPrinting={isPrinting} />
      )}
      {question.type === 'yes_no' && <YesNoChart answers={yesNoAnswers} isPrinting={isPrinting} />}
      {question.type === 'multiple_choice' && (
        <MultipleChoiceChart
          answers={mcAnswers}
          options={question.options ?? []}
          isPrinting={isPrinting}
        />
      )}
      {question.type === 'checkbox' && (
        <CheckboxChart
          answers={checkboxAnswers}
          options={question.options ?? []}
          isPrinting={isPrinting}
        />
      )}
      {question.type === 'open' && (
        <OpenResponsesView answers={openAnswers} isPrinting={isPrinting} />
      )}
    </Card>
  )
}
